import { GoogleIcon, InstagramIcon, TikTokIcon } from 'components/Icons';
import DefinitionTooltip from 'components/DefinitionTooltip';
import MetricIcon from 'components/HealthStats/MetricIcon';
import PricePointMetricIcon from 'components/HealthStats/PricePointMetricIcon';
import { getHealthStatDefinition } from 'utils/metrics';
import { getScoreColor } from 'utils/helpers';
import { tooltips } from '@/copy';

const healthStatTooltips = Object.entries(tooltips.healthStats).reduce(
    (acc, [metricLabel, { scoreLabels, scoreDefinitions }]) => {
        acc[metricLabel] = Object.entries(scoreLabels).reduce(
            (acc, [score, scoreLabel]) => {
                acc[scoreLabel] = {
                    definition: scoreDefinitions[score],
                    score: parseInt(score, 10),
                };
                return acc;
            },
            {},
        );
        return acc;
    },
    {},
);

function PlatformPopularityMetricIcon({ metricLabel, label }) {
    if (label?.toLowerCase() === 'google')
        return (
            <GoogleIcon
                metricLabel={metricLabel}
                css={`
                    color: ${({ theme }) => theme.google_blue};
                `}
            />
        );
    if (label?.toLowerCase() === 'instagram')
        return (
            <InstagramIcon
                metricLabel={metricLabel}
                css={`
                    color: ${({ theme }) => theme.instagram_orange};
                `}
            />
        );
    if (label?.toLowerCase() === 'tiktok')
        return (
            <TikTokIcon
                metricLabel={metricLabel}
                css={`
                    color: ${({ theme }) => theme.tiktok_pink};
                `}
            />
        );
    return <MetricIcon metricLabel={metricLabel} />;
}

function HealthStatTooltipIcon({ metricLabel, label }) {
    if (metricLabel === 'price point') {
        return (
            <PricePointMetricIcon
                score={healthStatTooltips[metricLabel][label]?.score}
                css={`
                    color: ${({ theme }) => theme.purple_1};
                    font-size: 0.9em;
                `}
            />
        );
    }

    if (metricLabel === 'popularity share') {
        return (
            <PlatformPopularityMetricIcon
                metricLabel={metricLabel}
                label={label}
            />
        );
    }

    return (
        <span
            css={`
                color: ${getScoreColor(
                    healthStatTooltips[metricLabel]?.[label]?.score,
                )};
            `}
        >
            <MetricIcon metricLabel={metricLabel} />
        </span>
    );
}

function HealthStatTooltip({ metricLabel, label, ...props }) {
    const def = getHealthStatDefinition(metricLabel);
    const scoreDefinition =
        healthStatTooltips[metricLabel]?.[label]?.definition ??
        'Insufficient data to calculate health stat.';
    return (
        <DefinitionTooltip
            icon={
                <HealthStatTooltipIcon
                    metricLabel={metricLabel}
                    label={label}
                />
            }
            title={label || 'Insufficient Data'}
            body={[scoreDefinition, def?.body].flat().filter(Boolean)}
            source={def?.source}
            sourceDataType={def?.sourceDataType}
            mixpanelMeta={{ contentKey: `${metricLabel}-${label}` }}
            {...props}
        />
    );
}

export default HealthStatTooltip;
